import { BaseViewStore, HyperlinkCellData } from 'core/stores';
import { action, observable, makeObservable } from 'mobx';
import { PalletCreateForm, PalletizeFulfillmentOrderForm } from 'modules/order/forms';
import { PalletStore, FulfillmentOrderStore } from 'modules/order/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { ConfirmModalParams } from 'core/models';
import { ApplicationStore } from 'platform/application/stores';
import { CarrierServiceStore } from 'modules/carrier/stores';
import {
	formInvalidate,
	notificationError,
	isSeller,
	isSuperAdminOrganization,
	isWarehouse,
	createFileUrl,
} from 'core/utils';
import { carrierServices, sourceOfChange } from 'common/constants';
import { SoundService } from 'modules/sound/services';
import moment from 'moment';
import _ from 'lodash';

class PalletizingModeViewStore extends BaseViewStore {
	@observable data = [];
	@observable barcode = null;
	@observable selectedCarrierService = null;
	@observable selectedPallet = null;
	@observable selectedWarehouse = null;
	@observable createPalletNotification = null;
	@observable palletFindNotification = null;
	@observable scanPackageNotification = null;
	@observable undoNotification = null;
	@observable lastAction = null;
	@observable newPallet = false;
	@observable label = null;
	@observable palletOptions = [];
	@observable warehouseOptions = [];
	@observable isWarehouseDropdownLoading = false;
	@observable isPalletDropdownLoading = false;
	@observable showActions = true;
	@observable page = 1;
	@observable infiniteSearchPhrase = null;
	@observable orderCheck = null;
	@observable selectedValue = null;
	@observable selectedLabel = null;
	debouncedWarehouseSearch = null;
	carrierServiceOptions = [];
	searchPhrase = null;
	barcodeRef = null;
	selectReference = null;
	warehouseReference = null;
	trackingNumber = null;
	barcodeInput = null;
	trackingNumberInput = null;
	pallet = null;
	debouncedPalletSearch = null;
	debouncedShipmentInfoSearch = null;
	originalBarcode = null;
	deletable = true;
	palletToDelete = { id: '', name: '' };

	palletCreateForm = new PalletCreateForm({
		onSuccess: this.createPallet,
	});

	palletizingForm = new PalletizeFulfillmentOrderForm();

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.parentView = rootStore.orderListViewStore;
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.palletStore = new PalletStore(rootStore);
		this.organizationStore = new ApplicationStore(rootStore);
		this.soundService = new SoundService();
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.isSeller = isSeller(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.defineTableData();
		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		let tasks = [];
		tasks.push(this.findCarrierServices());
		if (!this.isSeller && !this.isSuperAdminOrganization) {
			tasks.push(this.findPallets());
		}
		if (this.isSeller || this.isSuperAdminOrganization) {
			tasks.push(this.findWarehouses());
			this.debouncedWarehouseSearch = _.debounce((searchPhrase, { action }) => {
				this.searchWarehouseAsync(searchPhrase, { action });
			}, 500);
		}
		await Promise.all(tasks);
		this.debouncedPalletSearch = _.debounce((searchPhrase, { action }) => {
			this.searchPalletAsync(searchPhrase, { action });
		}, 1000);
		this.resume();
	}

	@action.bound
	async findCarrierServices() {
		await this.carrierServiceStore.findCarrierService({
			page: 1,
			rpp: 30,
			embed: ['carrier', 'carrierType', 'carrierServiceType'],
		});
		const filteredArray = _.filter(this.carrierServiceStore.data, item => {
			return carrierServices.indexOf(item.name) > -1;
		});
		this.carrierServiceOptions = _.map(filteredArray, item => {
			if (item.name === 'USPS Priority Mail Express') {
				item.name = 'Stamps Express';
			}
			if (item.name === 'USPS First Class Mail') {
				item.name = 'Stamps Ground';
			}
			return { value: item.id, label: item.name, extra: item };
		});
	}

	@action.bound
	async findPallets(pallet = null) {
		await this.palletStore.findPallet({
			page: 1,
			rpp: 100,
			isOpened: true,
			isSeller: this.isSeller || this.isSuperAdminOrganization,
			warehouseIds:
				(this.isSeller || this.isSuperAdminOrganization) && this.selectedWarehouse
					? [this.selectedWarehouse.value]
					: undefined,
			embed: [
				'shipmentInfos',
				'shipmentInfos.fulfillmentOrder',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderStatus',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems.product',
				'carrierService',
				'carrierService.carrierServiceType',
				'carrierService.carrierType',
			],
		});
		this.palletOptions = _.map(this.palletStore.data, item => {
			return { value: item.id, label: item.barcode, extra: item };
		});
		if (this.palletOptions.length === 1) {
			this.selectedPallet = this.palletOptions[0];
			this.palletizingForm.$('pallet.id').value = this.selectedPallet.value;
		}
		if (pallet != null) {
			this.selectedPallet = _.find(this.palletOptions, palletOption => {
				return palletOption.value == pallet.value;
			});
			this.palletizingForm.$('pallet.id').value = this.selectedPallet.value;
		}
	}

	@action.bound
	async findWarehouses() {
		await this.organizationStore.find({ page: 1, rpp: 15, warehouseOnly: true, isOrganizationActive: true });
		this.warehouseOptions = _.map(this.organizationStore.data, item => {
			return { value: item.id, label: item.applicationTitle };
		});
	}

	@action.bound
	async handleOnMenuScroll() {
		if (this.warehouseOptions.length < this.page * 15) return;
		this.isWarehouseDropdownLoading = true;
		this.page++;
		await this.organizationStore.find({
			search: this.infiniteSearchPhrase,
			page: this.page,
			rpp: 15,
			warehouseOnly: true,
			isOrganizationActive: true,
		});
		if (this.organizationStore.response.item.length > 0) {
			const newOptions = _.map(this.organizationStore.data, item => {
				return { value: item.id, label: item.applicationTitle };
			});
			this.warehouseOptions = this.warehouseOptions.concat(newOptions);
		}
		this.isWarehouseDropdownLoading = false;
	}

	@action.bound
	async onMenuClose() {
		this.page = 1;
		this.infiniteSearchPhrase = null;
		await this.findWarehouses();
	}

	@action.bound
	async searchWarehouseAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.page = 1;
		}
		if (action === 'input-change') {
			this.isWarehouseDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.page = 1;
			this.warehouseOptions = [];
			await this.organizationStore.find({
				search: searchPhrase || null,
				page: 1,
				rpp: 15,
				warehouseOnly: true,
				isOrganizationActive: true,
			});
			const newOptions = [];
			_.forEach(this.organizationStore.data, item => {
				newOptions.push({ value: item.id, label: item.applicationTitle, extra: item });
			});
			this.warehouseOptions = newOptions;
			this.isWarehouseDropdownLoading = false;
		}
	}

	@action.bound
	async searchPalletAsync(searchPhrase, { action }) {
		if (action === 'input-change') {
			this.isPalletDropdownLoading = true;
			this.data = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 50,
				orderBy: 'barcode',
				orderDirection: 'asc',
				searchFields: 'barcode',
				warehouseIds:
					(this.isSeller || this.isSuperAdminOrganization) && this.selectedWarehouse
						? [this.selectedWarehouse.value]
						: [],
				isSeller: this.isSeller || this.isSuperAdminOrganization,
				embed: [
					'shipmentInfos',
					'shipmentInfos.fulfillmentOrder',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderStatus',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems.product',
					'carrierService',
					'carrierService.carrierServiceType',
					'carrierService.carrierType',
				],
				isOpened: true,
			};
			await this.palletStore.findPallet(filter);
			var newOptions = [];
			if (this.palletStore.response.totalRecords == 1 && searchPhrase != '') {
				let pallet = this.palletStore.data[0];
				this.palletFindNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.FOUND';
				newOptions.push({ value: pallet.id, label: pallet.barcode, extra: pallet });
				this.onPalletChange({ value: pallet.id, label: pallet.barcode, extra: pallet });
				this.transformData(pallet.shipmentInfos);
				this.palletFindNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.FOUND';
				if (this.selectReference != null) {
					this.selectReference.setState({ menuIsOpen: false });
				}
				await this.soundService.success();
			} else {
				if (this.palletStore.response.totalRecords === 0) {
					if (searchPhrase !== '') {
						this.palletFindNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.NOTFOUND';
						this.scanPackageNotification = null;
						await this.soundService.error();
					}
					this.selectedPallet = null;
					if (this.selectReference) {
						this.selectReference.select.setState({ menuIsOpen: false, inputValue: '' });
						this.selectReference.blur();
						this.selectReference.focus();
					}
					_.forEach(this.palletOptions, item => {
						newOptions.push(item);
					});
				} else {
					_.forEach(this.palletStore.data, item => {
						newOptions.push({ value: item.id, label: item.barcode, extra: item });
					});
				}
			}
			this.palletOptions = newOptions;
			this.isPalletDropdownLoading = false;
		}
	}

	@action.bound
	togglePallet() {
		this.data = [];
		this.palletCreateForm.clear();
		this.palletizingForm.$('trackingNumber').clear();
		this.selectedCarrier = null;
		this.palletFindNotification = null;
		this.scanPackageNotification = null;
		this.createPalletNotification = null;
		this.newPallet = !this.newPallet;
		this.undoNotification = null;
		if (this.newPallet) {
			this.palletCreateForm.$('name').value = moment().toString();
			this.generateBarcode();
		}
	}

	@action.bound
	generateBarcode() {
		var result = '';
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
		const charactersLength = characters.length;
		for (var i = 0; i < 10; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}
		this.barcode = result;
		this.originalBarcode = result;
	}

	@action.bound
	onCarrierServiceChange(item) {
		this.selectedCarrierService = item;
		this.palletCreateForm.$('carrierService.id').value = item.value;
		this.addBarcodePrefix(item);
	}

	@action.bound
	addBarcodePrefix(item) {
		if (item.extra.carrierType.abrv === 'fedex') {
			if (item.extra.carrierServiceType.abrv === 'ground') {
				this.barcode = `FDXG-${this.originalBarcode}`;
			} else {
				this.barcode = `FDXE-${this.originalBarcode}`;
			}
		}
		if (item.extra.carrierType.abrv === 'ups') {
			if (item.extra.carrierServiceType.abrv === 'ground') {
				this.barcode = `UPSG-${this.originalBarcode}`;
			} else {
				this.barcode = `UPSE-${this.originalBarcode}`;
			}
		}

		if (item.extra.carrierType.abrv === 'stamps') {
			if (item.extra.carrierServiceType.abrv === 'ground') {
				this.barcode = `STAMPSG-${this.originalBarcode}`;
			} else {
				this.barcode = `STAMPSE-${this.originalBarcode}`;
			}
		}
	}

	@action.bound
	async onPalletChange(item) {
		this.data = [];
		this.undoNotification = null;
		this.palletFindNotification = null;
		this.scanPackageNotification = null;
		this.createPalletNotification = null;
		this.selectedPallet = item;
		this.palletizingForm.$('pallet.id').value = item.value;
		this.palletFindNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.FOUND';
		await this.soundService.success();
		if (this.trackingNumberInput != null) {
			if (this.trackingNumberInput.disabled) {
				this.trackingNumberInput.disabled = false;
			}
			this.trackingNumberInput.focus();
		}
		this.transformData(item.extra.shipmentInfos);
	}

	@action.bound
	async onWarehouseChange(item) {
		this.data = [];
		this.palletOptions = [];
		this.selectedPallet = null;
		this.undoNotification = null;
		this.palletFindNotification = null;
		this.scanPackageNotification = null;
		this.createPalletNotification = null;
		this.selectedWarehouse = item;
		if (item != null) {
			this.palletizingForm.$('warehouse.id').value = item.value;
			await this.findPallets();
		} else {
			this.palletizingForm.$('warehouse.id').value = '';
			this.warehouseReference.focus();
		}
	}

	@action.bound
	async createPallet() {
		try {
			let {
				name,
				carrierService: { id },
			} = this.palletCreateForm.values();
			let { warehouse } = this.palletizingForm.values();
			if (this.barcodeRef != null) {
				var label = this.barcodeRef.refs.renderElement.currentSrc.split(',')[1];
			}
			const response = await this.palletStore.create(
				{
					warehouseId: warehouse.id,
					carrierServiceId: id,
					barcode: this.barcode,
					name,
					open: true,
					label,
				},
				sourceOfChange.palletizingModeCreatePallet
			);
			if (response.hasOwnProperty('statusCode')) {
				this.rootStore.notificationStore.success('FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PRINTJOBCREATED');
			} else {
				this.rootStore.notificationStore.warning('FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PRINTNODEINFONOTFOUND');
			}
			this.palletCreateForm.$('carrierService').value = {};
			this.newPallet = false;
			this.palletFindNotification = null;
			if (response.hasOwnProperty('pallets')) {
				this.pallet = response.pallets[0];
			} else {
				this.pallet = response[0];
			}
			this.onPalletChange({ value: this.pallet.id, label: this.pallet.barcode, extra: this.pallet });
			this.undoNotification = null;
			this.createPalletNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.SUCCESS';
			this.selectedCarrierService = null;
			this.pendingLoadingSuspend();
			await this.findPallets();
			this.resume();
			if (this.trackingNumberInput != null) {
				this.trackingNumberInput.focus();
			}
		} catch (err) {
			if (err.statusCode == 409) {
				this.createPalletNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.CONFLICT';
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	}

	@action.bound
	async onKeyUp(event) {
		if (event.currentTarget.value.length != 0) {
			const valueWithoutWhitespace = event.currentTarget.value.replace(/ |\t/g, ''); // Remove whitespace and tabs.
			this.palletizingForm.$('trackingNumber').value = valueWithoutWhitespace;
			if (!this.debouncedShipmentInfoSearch) {
				this.debouncedShipmentInfoSearch = _.debounce(() => {
					this.findShipmentInfo();
				}, 500);
			}

			this.debouncedShipmentInfoSearch();
		} else {
			this.scanPackageNotification = null;
		}
	}

	@action.bound
	async findShipmentInfo() {
		try {
			const { trackingNumber, pallet } = this.palletizingForm.values();
			this.undoNotification = null;
			this.trackingNumber =
				trackingNumber.length === 34 ? trackingNumber.substring(trackingNumber.length - 12) : trackingNumber;
			await this.shipmentInfoStore.updateShipmentInfoByTrackingNumber(
				{
					trackingNumber: this.trackingNumber,
					palletId: pallet.id,
				},
				sourceOfChange.palletizingModeUpdateShipmentInfoByTrackingNumber
			);
			await this.shipmentInfoStore.find({
				page: 1,
				rpp: 100,
				isPalletizing: true,
				palletId: pallet.id,
				embed: [
					'fulfillmentOrder',
					'fulfillmentOrder.fulfillmentOrderStatus',
					'fulfillmentOrder.fulfillmentOrderItems',
					'fulfillmentOrder.fulfillmentOrderItems.orderLine',
					'fulfillmentOrder.fulfillmentOrderItems.orderLine.product',
					'fulfillmentOrder.fulfillmentOrderItems.orderLine.product.productVirtualSkus',
					'fulfillmentOrder.fulfillmentOrderItems.product',
				],
			});
			this.data = [];
			this.transformData(this.shipmentInfoStore.data);
			this.parentView.reload = true;
			this.scanPackageNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PACKAGESCAN.SUCCESS';
			await this.soundService.success();
			this.lastAction = 'palletize';
			this.undoNotification = null;
			if (this.selectedPallet != null) {
				this.selectedPallet.extra.shipmentInfos = this.shipmentInfoStore.data;
			}
			setTimeout(() => {
				if (!this.newPallet) {
					this.palletizingForm.$('trackingNumber').clear();
					if (this.trackingNumberInput != null) {
						this.trackingNumberInput.focus();
					}
				}
			}, 1500);
		} catch (err) {
			if (err.statusCode == 404) {
				this.scanPackageNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PACKAGESCAN.NOTFOUND';
				this.data = [];
			} else if (err.data && err.data.errorMessage != null) {
				this.scanPackageNotification = err.data.errorMessage;
			} else {
				formInvalidate({ form: this.palletizingForm, err });
			}
			await this.soundService.error();
		}
	}

	@action.bound
	setRef(ref) {
		if (ref != null) {
			if (ref.constructor.name === 'Async') {
				this.selectReference = ref;
			} else {
				this.barcodeRef = ref;
			}
		}
	}

	@action.bound
	setWarehouseRef(ref) {
		if (ref != null) {
			this.warehouseReference = ref;
		}
	}

	@action.bound
	async onUndo() {
		if (this.lastAction === 'palletize') {
			try {
				await this.shipmentInfoStore.undoPalletize(
					{ trackingNumber: this.trackingNumber, isFromLabelingMode: false },
					sourceOfChange.undoPalletizePalletizingMode
				);
				this.undoNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.UNDOSUCCESS';
				this.scanPackageNotification = null;
				this.lastAction = null;
				await this.shipmentInfoStore.find({
					page: 1,
					rpp: 100,
					palletId: this.selectedPallet.value,
					embed: [
						'fulfillmentOrder',
						'fulfillmentOrder.fulfillmentOrderStatus',
						'fulfillmentOrder.fulfillmentOrderItems',
						'fulfillmentOrder.fulfillmentOrderItems.product',
					],
				});
				if (this.selectedPallet != null) {
					this.selectedPallet.extra.shipmentInfos = this.shipmentInfoStore.data;
				}
				this.data = [];
				this.transformData(this.shipmentInfoStore.data);
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}
		}
	}

	@action.bound
	defineTableData() {
		this.tableData = {
			columns: [
				{
					key: 'fulfillmentOrder.fulfillmentOrderId',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.FULFILLMENTORDERID',
					dataCell: HyperlinkCellData,
					onClick: dataItem => this.openDetailPage(dataItem.id),
					selected: true,
					width: 80,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.fulfillmentOrderStatus.name',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.STATUS',
					selected: true,
					width: 120,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.product.sku',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.PRODUCTSKU',
					selected: true,
					width: 150,
					filter: null,
				},
			],
			actions: {
				onRemoveFromPallet: async item => this.openRemoveFromPalletModal(item),
			},
			sortObject: [
				{
					field: 'fulfillmentOrderId',
					dir: 'asc',
				},
			],
		};
		this.permissions = {
			update: () => this.rootStore.authStore.hasPermission('fulfillmentOrderSection.update'),
			create: () => this.rootStore.authStore.hasPermission('fulfillmentOrderSection.create'),
			remove: () => this.rootStore.authStore.hasPermission('fulfillmentOrderSection.delete'),
			read: () => this.rootStore.authStore.hasPermission('fulfillmentOrderSection.read'),
			full: () => this.rootStore.authStore.hasPermission('fulfillmentOrderSection.full'),
		};
	}

	@action.bound
	transformData(shipmentInfos) {
		_.each(shipmentInfos, shipmentInfo => {
			if (shipmentInfo != null && shipmentInfo.fulfillmentOrder != null) {
				let product = {};
				if (shipmentInfo.fulfillmentOrder.fulfillmentOrderItems.length != 1) {
					product.name = 'Multiple';
					product.sku = 'Multiple';
				} else {
					product.name = shipmentInfo.fulfillmentOrder.fulfillmentOrderItems[0].product.name;
					product.sku = shipmentInfo.fulfillmentOrder.fulfillmentOrderItems[0].product.mainSku;
				}
				shipmentInfo.fulfillmentOrder.product = product;
				this.data.push(shipmentInfo);
			}
		});
	}

	@action.bound
	setInputs(input, field) {
		const key = field.key + 'Input';
		this[key] = input;
	}

	@action.bound
	onPrintLabel() {
		if (this.selectedPallet.extra.label != '') {
			this.label = createFileUrl(this.selectedPallet.extra.label);

			setTimeout(() => {
				const frame = window.frames['pdf-print-modal-pallet'];
				if (frame != null) {
					frame.print();
				}
			}, 250);
		}
	}

	openDetailPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.list.detail', {
			id: id,
		});
	};

	removeFromPalletModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.removeFromPallet();
		},
	});

	@action.bound openRemoveFromPalletModal = item => {
		this.selectedItem = item;
		this.removeFromPalletModal.open({
			message: ['EXITSCANDETAILVIEW.MODAL.REMOVEFROMPALLET', { name: item.fulfillmentOrder.fulfillmentOrderId }],
		});
	};

	@action.bound async removeFromPallet() {
		try {
			this.data = _.filter(this.data, item => {
				return item.id != this.selectedItem.id;
			});
			const fulfillmentOrder = this.selectedItem.fulfillmentOrder;
			this.selectedItem.palletId = null;
			this.selectedItem.fulfillmentOrder = null;
			await this.shipmentInfoStore.updateShipmentInfo(
				this.selectedItem,
				true,
				sourceOfChange.palletizingModeRemoveFromPallet
			);
			this.rootStore.notificationStore.success([
				'EXITSCANDETAILVIEW.TABLE.ACTION.NOTIFICATION.REMOVEFROMPALLET',
				{ name: fulfillmentOrder.fulfillmentOrderId },
			]);
			await this.findPallets(this.selectedPallet);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	@action.bound async openDeleteConfirmModal(value, label) {
		this.selectedValue = value;
		this.selectedLabel = label;
		this.deleteConfirmModal.open({
			message: ['LOOKUPDELETECONFIRM.PALLET', { name: this.selectedLabel }],
		});
	}

	deleteConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.deletePallet(this.selectedValue, this.selectedLabel);
		},
	});

	@action.bound
	async deletePallet(value, label) {
		this.suspend();
		this.palletToDelete.id = value;
		this.palletToDelete.name = label;
		try {
			await this.palletStore.delete(this.palletToDelete);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.PALLET', { name: label }]);
			this.selectedPallet = null;
			this.palletFindNotification = null;
			await this.findPallets();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}
}

export default PalletizingModeViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/PalletizingModeViewStore.js